import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  DocumentIcon,
  CalendarIcon,
  UserIcon,
  ShareIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';
import Modal from '../../../../ui/canon/Modal';
import Button from '../../../../ui/canon/Button';

const DocumentDetailModal = ({
  open,
  document,
  onClose,
  onEdit,
  onDelete,
  onDownload,
}) => {
  const intl = useIntl();

  if (!document) return null;

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getCategoryColor = (category) => {
    const colors = {
      financial: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      legal: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      operational: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
      marketing: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
      hr: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
      other: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    };
    return colors[category] || colors.other;
  };

  const getSourceBadge = (sourceType) => {
    const badges = {
      direct_upload: { label: intl.formatMessage({ id: 'succession.vault.source_direct_upload' }), color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
      sprint_item: { label: intl.formatMessage({ id: 'succession.vault.source_sprint_item' }), color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
      sprint_program: { label: intl.formatMessage({ id: 'succession.vault.source_sprint_program' }), color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300' },
      sprint_enrollment: { label: intl.formatMessage({ id: 'succession.vault.source_sprint_enrollment' }), color: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300' },
      continuity_plan: { label: intl.formatMessage({ id: 'succession.vault.source_platform' }), color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
      cash_flow: { label: intl.formatMessage({ id: 'succession.vault.source_platform' }), color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
      valuation: { label: intl.formatMessage({ id: 'succession.vault.source_platform' }), color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
    };
    return badges[sourceType] || badges.direct_upload;
  };

  const sourceBadge = getSourceBadge(document.source_type);
  const unknown = intl.formatMessage({ id: 'succession.vault.unknown' });

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="bg-succession-bg-primary text-succession-text-primary rounded-lg shadow-succession-lg border border-succession-border w-full max-w-2xl animate-modal-scale-in"
    >
      <div className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-lg bg-succession-bg-secondary flex items-center justify-center">
              <DocumentIcon className="h-8 w-8 text-succession-text-secondary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-succession-text-primary mb-1">
                {document.title}
              </h2>
              <p className="text-sm text-succession-text-secondary">
                {document.description || intl.formatMessage({ id: 'succession.vault.detail_no_description' })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-hover-bg rounded-md transition-theme duration-theme"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-succession-text-primary mb-3 flex items-center gap-2">
              {intl.formatMessage({ id: 'succession.vault.detail_file_info' })}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-succession-text-secondary mb-1">
                  {intl.formatMessage({ id: 'succession.vault.detail_file_name' })}
                </p>
                <p className="text-sm text-succession-text-primary">
                  {document.file_name || document.document?.name || unknown}
                </p>
              </div>
              <div>
                <p className="text-xs text-succession-text-secondary mb-1">
                  {intl.formatMessage({ id: 'succession.vault.detail_file_size' })}
                </p>
                <p className="text-sm text-succession-text-primary">
                  {formatFileSize(document.file_size || document.document?.file_size)}
                </p>
              </div>
              <div>
                <p className="text-xs text-succession-text-secondary mb-1">
                  {intl.formatMessage({ id: 'succession.vault.detail_file_type' })}
                </p>
                <p className="text-sm text-succession-text-primary">
                  {(document.file_name || document.document?.name || '').split('.').pop()?.toUpperCase() || unknown}
                </p>
              </div>
              <div>
                <p className="text-xs text-succession-text-secondary mb-1">
                  {intl.formatMessage({ id: 'succession.vault.detail_source' })}
                </p>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-xs rounded-md ${sourceBadge.color}`}>
                    {sourceBadge.label}
                  </span>
                  {document.source_type === 'sprint_item' && document.sprint_name && (
                    <span className="text-xs text-succession-text-secondary">
                      • {document.sprint_name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-succession-border pt-6">
            <h3 className="text-sm font-semibold text-succession-text-primary mb-3 flex items-center gap-2">
              <TagIcon className="h-4 w-4" />
              {intl.formatMessage({ id: 'succession.vault.detail_metadata' })}
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-succession-text-secondary mb-1">
                  {intl.formatMessage({ id: 'succession.vault.detail_category' })}
                </p>
                <span className={`px-2 py-0.5 text-xs rounded-md ${getCategoryColor(document.category || 'other')}`}>
                  {document.category || intl.formatMessage({ id: 'succession.vault.cat_other' })}
                </span>
              </div>
              {document.tags && document.tags.length > 0 && (
                <div>
                  <p className="text-xs text-succession-text-secondary mb-1">
                    {intl.formatMessage({ id: 'succession.vault.detail_tags' })}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {document.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 text-xs bg-succession-bg-secondary text-succession-text-secondary rounded-md"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-succession-border pt-6">
            <h3 className="text-sm font-semibold text-succession-text-primary mb-3 flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              {intl.formatMessage({ id: 'succession.vault.detail_upload_info' })}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-succession-text-secondary mb-1 flex items-center gap-1">
                  <UserIcon className="h-3 w-3" />
                  {intl.formatMessage({ id: 'succession.vault.detail_uploaded_by' })}
                </p>
                <p className="text-sm text-succession-text-primary">
                  {document.uploaded_by_name || unknown}
                </p>
              </div>
              <div>
                <p className="text-xs text-succession-text-secondary mb-1 flex items-center gap-1">
                  <CalendarIcon className="h-3 w-3" />
                  {intl.formatMessage({ id: 'succession.vault.detail_upload_date' })}
                </p>
                <p className="text-sm text-succession-text-primary">
                  {document.created_at ? moment(document.created_at).format('MMMM D, YYYY') : unknown}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-succession-text-secondary mb-1 flex items-center gap-1">
                  <CalendarIcon className="h-3 w-3" />
                  {intl.formatMessage({ id: 'succession.vault.detail_last_modified' })}
                </p>
                <p className="text-sm text-succession-text-primary">
                  {document.updated_at ? moment(document.updated_at).format('MMMM D, YYYY') : unknown}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-succession-border pt-6">
            <h3 className="text-sm font-semibold text-succession-text-primary mb-3 flex items-center gap-2">
              <ShareIcon className="h-4 w-4" />
              {intl.formatMessage({ id: 'succession.vault.detail_sharing' })}
            </h3>
            <div>
              <p className="text-xs text-succession-text-secondary mb-1">
                {intl.formatMessage({ id: 'succession.vault.detail_status' })}
              </p>
              <span className={`px-2 py-0.5 text-xs rounded-md ${
                document.shared_with_negotiating
                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
              }`}>
                {document.shared_with_negotiating
                  ? intl.formatMessage({ id: 'succession.vault.detail_shared' })
                  : intl.formatMessage({ id: 'succession.vault.detail_private' })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-6 pt-4 border-t border-succession-border">
          {document.can_delete && (
            <Button
              variant="outline"
              onClick={() => {
                onDelete(document);
                onClose();
              }}
              className="flex items-center gap-2 text-red-600 dark:text-red-400 border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <TrashIcon className="h-4 w-4" />
              {intl.formatMessage({ id: 'succession.vault.action_delete' })}
            </Button>
          )}
          <div className={`flex gap-2 ${document.can_delete ? '' : 'ml-auto'}`}>
            <Button
              variant="outline"
              onClick={onDownload}
              className="flex items-center gap-2"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {intl.formatMessage({ id: 'succession.vault.action_download' })}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onEdit();
                onClose();
              }}
              className="flex items-center gap-2"
            >
              <PencilIcon className="h-4 w-4" />
              {intl.formatMessage({ id: 'succession.vault.action_edit' })}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

DocumentDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  document: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
};

export default DocumentDetailModal;
